import React from 'react';
import './TextoConImagenes.css';

/** Etiquetas HTML permitidas para texto enriquecido (negrita, cursiva, etc.) */
const ALLOWED_TAGS = ['b', 'strong', 'i', 'em', 'u', 'br', 'sub', 'sup'];

/**
 * Sanitiza HTML permitiendo solo etiquetas de formato seguras.
 * Elimina script, style y otras etiquetas peligrosas.
 */
function sanitizeRichText(html) {
  if (!html || typeof html !== 'string') return '';
  let safe = html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    .replace(/\bon\w+\s*=\s*["'][^"']*["']/gi, '')
    .replace(/javascript:/gi, '');
  safe = safe.replace(/<\/?([a-zA-Z][a-zA-Z0-9]*)(\s[^>]*)?>/g, (match, tagName) => {
    const lower = tagName.toLowerCase();
    if (ALLOWED_TAGS.includes(lower)) {
      return match.startsWith('</') ? `</${lower}>` : `<${lower}>`;
    }
    return '';
  });
  return safe;
}

/**
 * Componente para renderizar texto con recursos embebidos.
 * Soporta marcadores [IMG:1], [IMG:2], [TABLE:1], [TABLE:2], etc.
 * Soporta texto enriquecido: <b>, <strong>, <i>, <em>, <u>, <br>.
 * 
 * @param {string} texto - Texto que puede contener marcadores [IMG:id]/[TABLE:id] y HTML básico
 * @param {Array} imagenes - Array de objetos con {id, archivo_local, tipo}
 * @param {Array} tablasContenido - Array de objetos con {id, columnas, filas, jerarquia_filas, valores_reutilizables}
 * @param {string} basePath - Ruta base para las imágenes (por defecto '/imagenes/')
 */
function TextoConImagenes({ texto, imagenes = [], tablasContenido = [], basePath = '/imagenes/' }) {
  const resolveTableCell = (celda, valoresReutilizables = {}) => {
    if (celda !== null && typeof celda === 'object' && !Array.isArray(celda)) {
      const ref = celda.ref;
      const align = celda.align || null;
      if (ref && Object.prototype.hasOwnProperty.call(valoresReutilizables, ref)) {
        return {
          value: valoresReutilizables[ref],
          align: align || 'center',
          isRef: true
        };
      }
      return {
        value: celda.value ?? '',
        align,
        isRef: false
      };
    }

    return {
      value: celda ?? '',
      align: null,
      isRef: false
    };
  };

  if (!texto) return null;

  // Normalizar etiquetas HTML <br> a saltos de línea para procesamiento
  const textoNormalizado = String(texto)
    .replace(/<br\s*\/?>/gi, '\n');

  // Si no hay recursos embebidos, renderizar texto con HTML enriquecido
  if ((!imagenes || imagenes.length === 0) && (!tablasContenido || tablasContenido.length === 0)) {
    const withBreaks = textoNormalizado.replace(/\n/g, '<br>');
    const sanitized = sanitizeRichText(withBreaks);
    return (
      <div 
        className="texto-simple texto-enriquecido"
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
    );
  }

  // Crear un mapa de imágenes por ID para acceso rápido
  const imagenesMap = {};
  imagenes.forEach(img => {
    imagenesMap[img.id] = img;
  });

  // Crear mapa de tablas por ID para acceso rápido
  const tablasMap = {};
  tablasContenido.forEach(tabla => {
    tablasMap[tabla.id] = tabla;
  });

  // Dividir el texto por los marcadores [IMG:id] y [TABLE:id]
  const partes = [];
  const regex = /\[(IMG|TABLE):(\d+)\]/g;
  let ultimoIndice = 0;
  let match;

  while ((match = regex.exec(textoNormalizado)) !== null) {
    // Agregar texto antes del marcador
    if (match.index > ultimoIndice) {
      const textoAntes = textoNormalizado.substring(ultimoIndice, match.index);
      if (textoAntes) {
        partes.push({ tipo: 'texto', contenido: textoAntes });
      }
    }

    const tipoMarcador = match[1];
    const recursoId = parseInt(match[2], 10);

    if (tipoMarcador === 'IMG') {
      const imagen = imagenesMap[recursoId];
      if (imagen) {
        partes.push({
          tipo: 'imagen',
          contenido: imagen,
          id: recursoId
        });
      } else {
        // Si no se encuentra la imagen, mostrar el marcador como texto
        partes.push({ tipo: 'texto', contenido: match[0] });
      }
    } else {
      const tabla = tablasMap[recursoId];
      if (tabla) {
        partes.push({
          tipo: 'tabla',
          contenido: tabla,
          id: recursoId
        });
      } else {
        // Si no se encuentra la tabla, mostrar el marcador como texto
        partes.push({ tipo: 'texto', contenido: match[0] });
      }
    }

    ultimoIndice = match.index + match[0].length;
  }

  // Agregar el texto restante después del último marcador
  if (ultimoIndice < textoNormalizado.length) {
    const textoRestante = textoNormalizado.substring(ultimoIndice);
    if (textoRestante) {
      partes.push({ tipo: 'texto', contenido: textoRestante });
    }
  }

  // Si no se encontraron marcadores, renderizar texto con HTML enriquecido
  if (partes.length === 0) {
    const withBreaks = textoNormalizado.replace(/\n/g, '<br>');
    const sanitized = sanitizeRichText(withBreaks);
    return (
      <div 
        className="texto-simple texto-enriquecido"
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
    );
  }

  return (
    <div className="texto-con-imagenes">
      {partes.map((parte, index) => {
        if (parte.tipo === 'imagen') {
          const rutaImagen = `${basePath}${parte.contenido.archivo_local}`;
          return (
            <div key={`img-${parte.id}-${index}`} className="imagen-embebida">
              <img 
                src={rutaImagen} 
                alt={`Imagen ${parte.id}`}
                className="imagen-pregunta"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          );
        } else if (parte.tipo === 'tabla') {
          const tabla = parte.contenido || {};
          const columnas = Array.isArray(tabla.columnas) ? tabla.columnas : [];
          const filas = Array.isArray(tabla.filas) ? tabla.filas : [];
          const jerarquiaFilas = Array.isArray(tabla.jerarquia_filas) ? tabla.jerarquia_filas : [];
          const valoresReutilizables =
            tabla.valores_reutilizables && typeof tabla.valores_reutilizables === 'object'
              ? tabla.valores_reutilizables
              : {};

          return (
            <div key={`tabla-${parte.id}-${index}`} className="tabla-embebida">
              <div className="tabla-embebida-wrapper">
                <table className="tabla-pregunta">
                  {columnas.length > 0 && (
                    <thead>
                      <tr>
                        {columnas.map((columna, colIndex) => (
                          <th key={`th-${parte.id}-${colIndex}`}>{columna}</th>
                        ))}
                      </tr>
                    </thead>
                  )}
                  <tbody>
                    {jerarquiaFilas.length > 0
                      ? jerarquiaFilas.flatMap((grupo, grupoIndex) => {
                          const groupRows = Array.isArray(grupo.filas) ? grupo.filas : [];
                          const groupAlign = grupo.align || 'center';

                          return groupRows.map((fila, filaIndex) => (
                            <tr key={`tr-g-${parte.id}-${grupoIndex}-${filaIndex}`}>
                              {filaIndex === 0 && (
                                <td
                                  rowSpan={groupRows.length}
                                  className={
                                    groupAlign === 'right'
                                      ? 'table-cell-right table-cell-group'
                                      : 'table-cell-center table-cell-group'
                                  }
                                >
                                  {grupo.grupo}
                                </td>
                              )}
                              {Array.isArray(fila)
                                ? fila.map((celda, celdaIndex) => {
                                    const cell = resolveTableCell(celda, valoresReutilizables);
                                    const alignClass =
                                      cell.align === 'center'
                                        ? 'table-cell-center'
                                        : cell.align === 'right'
                                          ? 'table-cell-right'
                                          : '';
                                    const refClass = cell.isRef ? 'table-cell-ref' : '';
                                    const className = [alignClass, refClass].filter(Boolean).join(' ');
                                    return (
                                      <td
                                        key={`td-g-${parte.id}-${grupoIndex}-${filaIndex}-${celdaIndex}`}
                                        className={className || undefined}
                                      >
                                        {cell.value}
                                      </td>
                                    );
                                  })
                                : null}
                            </tr>
                          ));
                        })
                      : filas.map((fila, filaIndex) => (
                          <tr key={`tr-${parte.id}-${filaIndex}`}>
                            {Array.isArray(fila)
                              ? fila.map((celda, celdaIndex) => {
                                  const cell = resolveTableCell(celda, valoresReutilizables);
                                  const alignClass =
                                    cell.align === 'center'
                                      ? 'table-cell-center'
                                      : cell.align === 'right'
                                        ? 'table-cell-right'
                                        : '';
                                  const refClass = cell.isRef ? 'table-cell-ref' : '';
                                  const className = [alignClass, refClass].filter(Boolean).join(' ');
                                  return (
                                    <td
                                      key={`td-${parte.id}-${filaIndex}-${celdaIndex}`}
                                      className={className || undefined}
                                    >
                                      {cell.value}
                                    </td>
                                  );
                                })
                              : null}
                          </tr>
                        ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        } else {
          // Renderizar texto con HTML enriquecido preservando saltos de línea
          const withBreaks = parte.contenido.replace(/\n/g, '<br>');
          const sanitized = sanitizeRichText(withBreaks);
          return (
            <span 
              key={`texto-${index}`} 
              className="texto-parte texto-enriquecido"
              dangerouslySetInnerHTML={{ __html: sanitized }}
            />
          );
        }
      })}
    </div>
  );
}

export default TextoConImagenes;

